import * as React from "react";
import * as THREE from 'three';

import { Rhino3dmLoader } from 'three/examples/jsm/loaders/3DMLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { TDSLoader } from 'three/examples/jsm/loaders/TDSLoader.js';
import { ThreeMFLoader } from 'three/examples/jsm/loaders/3MFLoader.js';
import { AMFLoader } from 'three/examples/jsm/loaders/AMFLoader.js';
import { IFCLoader } from 'three/examples/jsm/loaders/IFCLoader.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GCodeLoader } from 'three/examples/jsm/loaders/GCodeLoader.js';
import { KMZLoader } from 'three/examples/jsm/loaders/KMZLoader.js';
import { LDrawLoader } from 'three/examples/jsm/loaders/LDrawLoader.js';
import { LDrawUtils } from 'three/examples/jsm/utils/LDrawUtils.js';
import { MMDLoader } from 'three/examples/jsm/loaders/MMDLoader.js';
import { PCDLoader } from 'three/examples/jsm/loaders/PCDLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { TiltLoader } from 'three/examples/jsm/loaders/TiltLoader.js';
import { VOXLoader, VOXMesh } from 'three/examples/jsm/loaders/VOXLoader.js';
import { VTKLoader } from 'three/examples/jsm/loaders/VTKLoader.js';
import { XYZLoader } from 'three/examples/jsm/loaders/XYZLoader.js';
import IK_OrbitControls from './components/IK_OrbitControls';

import './css/App.scss';

interface IViewer
{
	url:string,
}

class Viewer extends React.Component<IViewer>
{
	private scene:THREE.Scene;
	private camera:THREE.PerspectiveCamera;
	private renderer:THREE.WebGLRenderer;
	private controls:IK_OrbitControls
	
	private container:React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
	private viewhelper:React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

	private clock = new THREE.Clock();

	//private fileUrl:string ="./models/3dm/Rhino_Logo.3dm";
	//private fileUrl:string ="./models/3ds/portalgun/portalgun.3ds";
	//private fileUrl:string ="./models/gltf/LittlestTokyo.glb";
	//private fileUrl:string ="./models/3mf/truck.3mf";
	//private fileUrl:string ="models/draco/bunny.drc"
	//private fileUrl:string ="./models/amf/rook.amf";
	//private fileUrl:string = "./models/ifc/rac_advanced_sample_project.ifc";
	//private fileUrl:string = "./models/collada/elf/elf.dae";
	//private fileUrl:string = "./models/collada/abb_irb52_7_120.dae";
	//private fileUrl:string = "./models/collada/stormtrooper/stormtrooper.dae";
	//private fileUrl:string = "./models/fbx/body.fbx";
	//private fileUrl:string = "./models/gcode/benchy.gcode";
	//private fileUrl:string = "./models/kmz/box.kmz";
	//private fileUrl:string = "./models/ldraw/officialLibrary/models/30054-1-AT-ST-Mini.mpd_Packed.mpd";
	//private fileUrl:string = "./models/mmd/miku/miku_v2.pmd";
	//private fileUrl:string ="./models/pcd/binary/Zaghetto.pcd";
	//private fileUrl:string ="./models/ply/binary/Lucy100k.ply";
	//private fileUrl:string ="./models/stl/ascii/slotted_disk.stl";
	//private fileUrl:string ="./models/tilt/BRUSH_DOME.tilt";
	//private fileUrl:string ="./models/vox/monu10.vox";
	//private fileUrl:string ="./models/vtk/bunny.vtk";
	private fileUrl:string="./models/xyz/helix_201.xyz";
	private serverPath:string;

	private mixer:THREE.AnimationMixer;

	constructor(prop:IViewer)
	{
		super(prop);
		
		this.fileUrl = prop.url; 
	}

	componentDidMount()
	{
		this.init();
		this.loader();
	}

	private init = ():void=>
	{
		this.scene = new THREE.Scene();
		THREE.Object3D.DefaultUp = new THREE.Vector3( 0, 0, 1 );
		this.renderer = new THREE.WebGLRenderer( { antialias: true } );
		this.renderer.setPixelRatio( window.devicePixelRatio );
		this.renderer.setSize( window.innerWidth, window.innerHeight );
		this.renderer.outputEncoding = THREE.sRGBEncoding;

		this.container.current.appendChild( this.renderer.domElement );
		this.camera = new THREE.PerspectiveCamera( 45, window.innerWidth / window.innerHeight, 1, 10000 );
		this.camera.position.set( 0, 0, 0 );

		const directionalLight = new THREE.DirectionalLight( 0xffffff, 1 );
		directionalLight.position.set( 100, -900, -100 );
		this.scene.add( directionalLight );

		const ambientLight = new THREE.AmbientLight( 0xffffee, 0.25 );
		this.scene.add( ambientLight );

		const pmremGenerator = new THREE.PMREMGenerator( this.renderer );
		this.scene.background = new THREE.Color( 0xbfe3dd );
		this.scene.environment = pmremGenerator.fromScene( new RoomEnvironment(), 0.04 ).texture;

		this.controls = new IK_OrbitControls( this.camera, this.renderer.domElement );
		this.controls.addEventListener ( "change" , this.render);
		this.controls.mouseButtons = { ORBIT:THREE.MOUSE.LEFT , ZOOM:THREE.MOUSE.MIDDLE, PAN:THREE.MOUSE.RIGHT };
		
		this.controls.target.set( 0, 0.5, 0 );
		//this.controls.enablePan = false;
		this.controls.enableDamping = true;

		this.event();
		this.animate();
	}

	private event = ():void =>
	{
		window.addEventListener( 'resize', this.onWindowResize );
	}

	private loader = (url:string =null):void =>
	{
		const ext:string = this.fileUrl.split('.').pop().toLowerCase();
		let loader:any;
		switch( ext ) 
		{
			case "3dm":
				
				loader = new Rhino3dmLoader();
				loader.setLibraryPath('https://cdn.jsdelivr.net/npm/rhino3dm@7.11.1/');
				loader.load( this.fileUrl,  ( object )=>
				{
					this.scene.add ( object );
					this.controls.focus ( object);
					this.controls.target.copy(object.position);
					this.onComplete();
				}, this.onProgress);
			break;
			case "3ds":
				loader = new TDSLoader( );
				loader.load(this.fileUrl, ( object ) =>
				{
					object.traverse(  ( child:THREE.Mesh ) =>
					{
						if ( child.isMesh )
						{
							(child.material as any).specular.setScalar( 0.1 );
						}
					});
					this.scene.add( object );
					this.controls.focus ( object);
					this.controls.target.copy(object.position);
					this.onComplete();
				}, this.onProgress);
			break;
			case "3mf":
				const manager = new THREE.LoadingManager();
				loader = new ThreeMFLoader(manager);
				loader.load ( this.fileUrl , (object ):void =>
				{
					object.traverse( ( child )=>
					{
						child.castShadow = true;
					});

					this.scene.add(object);
					this.controls.focus(object);
					this.onComplete();
				}, this.onProgress);
			break;
			case "amf":
				loader = new AMFLoader();
				loader.load ( this.fileUrl , (object) =>
				{
					this.scene.add ( object);
					this.controls.focus ( object);
					this.onComplete();
				}, this.onProgress);
			break;
			case "glb":
				const dracoLoader = new DRACOLoader();
				const gltfLoader = new GLTFLoader();
				dracoLoader.setDecoderPath( 'js/libs/draco/gltf/' );
				gltfLoader.setDRACOLoader( dracoLoader );
				gltfLoader.load( this.fileUrl, ( gltf ) =>
				{
					console.log( "gltf : " , gltf );
					const model = gltf.scene;
					model.rotateX(Math.PI/2);
					this.scene.add( model );
					
					this.controls.target.copy(model.position);
					this.controls.focus ( model);

					if( gltf.animations[ 0 ] )
					{
						this.mixer = new THREE.AnimationMixer( model );
						this.mixer.clipAction( gltf.animations[ 0 ] ).play();
					}
					this.onComplete();
				}, this.onProgress , (e)=>{
					
				});
			break;
			case "drc":
				loader = new DRACOLoader();
				loader.setDecoderPath('js/libs/draco/');
				loader.setDecoderConfig({ type: 'js' });
				loader.load(this.fileUrl, (geometry:THREE.BufferGeometry) =>
				{
					geometry.computeVertexNormals();
					const material = new THREE.MeshStandardMaterial( { color: 0x606060 } );
					const mesh = new THREE.Mesh( geometry, material );
					mesh.castShadow = true;
					mesh.receiveShadow = true;
					mesh.rotateX(Math.PI/2);
					this.controls.focus ( mesh);
					this.scene.add( mesh );
					this.onComplete();
				}, this.onProgress);
			break;
			case "ifc":
				loader = new IFCLoader();
				loader.ifcManager.setWasmPath( 'js/libs//ifc/' );
				loader.load( this.fileUrl, ( model ) =>
				{
					this.scene.add( model.mesh );
					model.mesh.rotateX(Math.PI/2);
					this.controls.focus ( model.mesh);
					this.onComplete();
				} ,this.onProgress);
			break;
			case "dae":
				loader = new ColladaLoader();
				loader.load ( this.fileUrl , (model:any) =>
				{
					this.scene.add( model.scene );
					model.scene.rotateX(Math.PI/2);
					model.scene.traverse( ( child )=>
					{
						if ( child.isMesh )
						{
							child.material.flatShading = true;
						}
					});
					const animations = model.scene.animations;
					if( animations[0])
					{
						this.mixer = new THREE.AnimationMixer(model.scene);
						this.mixer.clipAction(animations[0]).play();
					}
					this.controls.focus ( model.scene);;
					this.onComplete();
				},this.onProgress);
			break;
			case "fbx":
				loader = new FBXLoader();
				loader.load ( this.fileUrl  , (object)=>
				{
					this.mixer = new THREE.AnimationMixer( object );
					object.rotateX(Math.PI/2);
					if( object.animations[ 0 ] ) 
					{
						const action = this.mixer.clipAction( object.animations[ 0 ] );
						action.play();
					}

					object.traverse( ( child:any ) =>
					{
						if ( child.isMesh )
						{
							child.castShadow = true;
							child.receiveShadow = true;
						}
					});
					this.controls.focus ( object);;
					this.scene.add( object );
					this.onComplete();
				}, this.onProgress );
			break;
			case "gcode":
				loader = new GCodeLoader();
				loader.load ( this.fileUrl , (object)=>
				{
					object.rotateX(Math.PI/2);
					this.scene.add ( object);
					this.controls.focus ( object);;
					this.onComplete();
				}, this.onProgress);
			break;
			case "kmz":
				loader = new KMZLoader();
				loader.load( this.fileUrl, ( object )=> {
					this.scene.add ( object.scene);
					this.controls.focus ( object.scene);;
					this.onComplete();

				} ,this.onProgress);
			case "mpd":
				loader = new LDrawLoader();
				loader.load ( this.fileUrl , (group)=>
				{
					var model = group;
					model = LDrawUtils.mergeObject( model );
					model.rotateX(-Math.PI/2);
					
					this.scene.add( model );
					this.controls.focus ( model);;
					this.onComplete();
				},this.onProgress);
			break;
			case "pmd":
				loader = new MMDLoader();
				loader.load ( this.fileUrl , (object)=>
				{
					this.scene.add ( object );
					object.rotateX(Math.PI/2);
					this.controls.focus ( object);;
					this.onComplete();
				},this.onProgress);
			break;
			case "pcd":
				loader = new PCDLoader();
				loader.load( this.fileUrl , (object)=>
				{
					this.scene.add(object);
					object.rotateX(-Math.PI/2);
					this.controls.focus ( object );
					this.onComplete();
				}, this.onProgress);
			break;
			case "ply":
				loader = new PLYLoader();
				loader.load(this.fileUrl , (geometry)=>
				{
					geometry.computeVertexNormals();
					const material = new THREE.MeshStandardMaterial( { color: 0x0055ff, flatShading: true } );
					const mesh = new THREE.Mesh( geometry, material );
					mesh.castShadow = true;
					mesh.receiveShadow = true;
					mesh.rotateX(Math.PI/2);
					this.scene.add( mesh );
					this.controls.focus ( mesh );
					this.onComplete();
				},this.onProgress);
			break;
			case "stl":
				loader = new STLLoader();
				loader.load ( this.fileUrl , (geometry )=>
				{
					const material = new THREE.MeshPhongMaterial( { color: 0xff5533, specular: 0x111111, shininess: 200 } );
					const mesh = new THREE.Mesh( geometry, material );
					mesh.castShadow = true;
					mesh.receiveShadow = true;
					mesh.rotateX(Math.PI/2);
					this.scene.add( mesh );
					this.controls.focus ( mesh );
					this.onComplete();
				});
			break;
			case "tilt" : 
				loader = new TiltLoader();
				loader.load(  this.fileUrl , ( object )=>
				{
					// console.log( object.children.length );
					this.controls.focus ( object );
					this.scene.add( object );
					this.onComplete();
				} ,this.onProgress);
			break;
			case "vox":
				loader = new VOXLoader();
				loader.load( this.fileUrl, (chunks )=>
				{
					var object = new THREE.Object3D();
					object.rotateX(Math.PI/2);
					this.scene.add ( object );
					for ( let i = 0; i < chunks.length; i ++ ) {

						const chunk = chunks[ i ];
						// displayPalette( chunk.palette );
						const mesh = new VOXMesh( chunk );
						object.add( mesh );
					}
					this.controls.focus(object)
					this.onComplete();
				},this.onProgress);
			break;
			case "vtk":
				loader = new VTKLoader();
				loader.load ( this.fileUrl, (geometry)=>
				{
					geometry.computeVertexNormals();
					geometry.center();
					geometry.rotateX(Math.PI/2);
					const material = new THREE.MeshLambertMaterial( { color: 0xff0000 } );
					const mesh = new THREE.Mesh( geometry, material );

					this.scene.add( mesh );
					this.controls.focus(mesh)
					this.onComplete();	
				},this.onProgress);
			break;
			case "xyz":
				loader = new XYZLoader();
				loader.load ( this.fileUrl , (geometry)=>
				{
					geometry.center();
					//const vertexColors = ( geometry.hasAttribute( 'color' ) === true );
					//{ size: 0.1, vertexColors: vertexColors }
					const material = new THREE.PointsMaterial();

					var points = new THREE.Points( geometry, material );
					this.scene.add( points );
					this.controls.focus(points)
				}, this.onProgress)
			break;
		}
		this.renderer.render( this.scene, this.camera );
	}

	private onProgress = (event):void=>
	{
		console.log( " event : " , event.total , Math.round(event.loaded/event.total*100) );
	}

	private onComplete = ():void=>
	{
		console.log( 'onComplete');
	}

	private animate = ():void =>
	{
		if( this.mixer)
		{
			const delta = this.clock.getDelta();
			this.mixer.update( delta );
		}

		this.controls.update();
		this.renderer.render( this.scene, this.camera );
		requestAnimationFrame( this.animate);
	}

	private onWindowResize = (event:Event):void => 
	{
		try{
			const width = this.container.current.clientWidth;
			const height = this.container.current.clientHeight;
			
	
			this.camera.aspect = width / height;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize( width, height );
			this.renderer.render( this.scene , this.camera );
		}catch(e){console.log("error", e ) }
		
	}

	public render = ():React.ReactElement =>
	{
		return(<>
					<div className = "viewHelper" ref = {this.viewhelper}></div>
					<div className = "canvasCont" ref = {this.container}></div>
				</>)
	}
}
export default Viewer;